import type { CapiProductPage } from 'types/capi/pdp';

import ArrowIcon from '~/assets/icons/Arrow';
import ChevronIcon from '~/assets/icons/Chevron';
import SizeInfo from '~/components/SizeInfo';
import SizeSelectionEvent from '~/events/SizeSelectionEvent';
import usePageData from '~/hooks/usePageData';
import useRootData from '~/hooks/useRootData';

import SizeSelectorGrid from './SizeSelectorGrid';

type Props = {
  forceShowSizeGrid?: boolean;
  selectedSize?: string;
  setSelectedSize: React.Dispatch<React.SetStateAction<string | undefined>>;
  setShowSizeGrid: React.Dispatch<React.SetStateAction<boolean>>;
  shouldShowSizeGrid: boolean;
  showSizeGrid: boolean;
  sizes: string[];
};

export default function SizeSelection({
  forceShowSizeGrid,
  shouldShowSizeGrid,
  selectedSize,
  setSelectedSize,
  setShowSizeGrid,
  showSizeGrid,
  sizes
}: Props) {
  const product = usePageData<CapiProductPage>();
  const { isBot } = useRootData();

  const onGridToggleClick = () => {
    setShowSizeGrid(!showSizeGrid);
  };

  const onSizeSelect = (size?: string) => {
    setSelectedSize(size);
    setShowSizeGrid(!size);
    new SizeSelectionEvent({ isBot, pageType: 'pdp' }).send();
  };

  const show = Boolean(showSizeGrid || forceShowSizeGrid);

  return shouldShowSizeGrid ? (
    <>
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-bold">Select your UK size</h2>
        {!forceShowSizeGrid && !show && (
          <GridToggle onClick={onGridToggleClick} />
        )}
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          {product.size_fit_guide && (
            <div
              className={`flex w-full flex-col items-center justify-center gap-4 ${product.allow_size_selection ? 'rounded-t-lg pt-4 px-4' : 'rounded-lg p-4'} ${product.size_fit_guide?.adjustment === 0 ? 'bg-grey-tile' : 'bg-yellow-label p-4'}`}
            >
              <SizeInfo sizeFitGuide={product.size_fit_guide} />
            </div>
          )}
          <div
            className={`flex flex-col bg-grey-panel gap-4 p-4 ${product.size_fit_guide ? 'rounded-b-lg' : 'rounded-lg'}`}
          >
            <SizeSelectorGrid
              onSizeSelect={onSizeSelect}
              selectedSize={selectedSize}
              isExpanded={show}
              sizes={sizes}
            />
          </div>
        </div>
        {!selectedSize && (
          <div className="flex p-2 gap-2 bg-yellow-label text-sm tracking-wide">
            <span className="rotate-90">
              <ArrowIcon />
            </span>
            <span>Select your size for the best results</span>
          </div>
        )}
      </div>
    </>
  ) : (
    <SimplifiedSizeGuide product={product} />
  );
}

function SimplifiedSizeGuide({ product }: { product: CapiProductPage }) {
  if (!product.size_fit_guide) return null;

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-lg font-bold">Size guide</h2>
      <div
        className={`flex w-full flex-col items-center justify-center gap-4 p-4 rounded-lg ${product.size_fit_guide?.adjustment.toString() === '0' ? 'bg-grey-tile' : 'bg-yellow-label p-4'}`}
      >
        <SizeInfo sizeFitGuide={product.size_fit_guide} />
      </div>
    </div>
  );
}

function GridToggle({ onClick }: { onClick: () => void }) {
  return (
    <button className="flex items-center gap-1" onClick={onClick}>
      <div className="text-xs text-grey-hide underline tracking-wide">
        Expand all sizes
      </div>
      <ChevronIcon height="6" stroke="#A9A9A9" width="9" />
    </button>
  );
}
